import { Link } from '@inertiajs/react'
import { SearchX } from 'lucide-react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import BlogSidebar from '@/components/marketing/BlogSidebar'
import BlogSearchForm from '@/components/marketing/BlogSearchForm'
import { formatDate } from '@/components/marketing/BlogPostCard'
import { type CategoryNavItem } from '@/components/marketing/BlogCategoryNav'
import type { SharedMarketingProps } from '@/types/marketing'

export interface BlogSearchHit {
  slug: string
  title: string
  description: string
  /** Postgres ts_headline snippet — only <mark> tags survive sanitisation. */
  snippet_html: string | null
  category_name: string | null
  category_slug: string | null
  published_at: string | null
  reading_time: string | null
}

interface BlogSearchProps extends SharedMarketingProps {
  q: string
  results: BlogSearchHit[]
  categories: CategoryNavItem[]
}

export default function BlogSearch({ q, results, categories }: BlogSearchProps) {
  return (
    <>
      <AtmosphericHero minHeight="40vh">
        <div className="mx-auto max-w-3xl space-y-8 text-center">
          <ScrollReveal>
            <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>Blog search</span>
          </ScrollReveal>
          <ScrollReveal delay={0.05}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
              {q ? (
                <>
                  Results for &ldquo;<span className="text-[#ff6a1a]">{q}</span>&rdquo;
                </>
              ) : (
                'Search the blog'
              )}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <BlogSearchForm initialQuery={q} autoFocus size="lg" />
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      {/* Same sidebar + content layout as /blogs so search feels like part
          of the blog, not a separate surface. */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid gap-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14">
          <BlogSidebar categories={categories} hideActiveState />

          <div>
            {q && (
              <p className="text-sm text-white/40" aria-live="polite">
                {results.length === 0
                  ? 'No articles found'
                  : `${results.length} ${results.length === 1 ? 'article' : 'articles'} found`}
              </p>
            )}

            {results.length > 0 && (
              <ol className="mt-6 space-y-4" aria-label="Search results">
                {results.map((hit, i) => (
                  <ScrollReveal key={hit.slug} delay={Math.min(i, 8) * 0.04}>
                    <li>
                      <SearchResultRow hit={hit} />
                    </li>
                  </ScrollReveal>
                ))}
              </ol>
            )}

            {q && results.length === 0 && <EmptyState />}

            {!q && (
              <p className="text-white/48">
                Type a topic above — royalties, playlists, Content ID, release dates — and
                we&rsquo;ll search every article on the blog.
              </p>
            )}
          </div>
        </div>
      </section>
    </>
  )
}

function SearchResultRow({ hit }: { hit: BlogSearchHit }) {
  return (
    <Link
      href={`/guides/${hit.slug}`}
      prefetch
      className="group block rounded-2xl border border-white/8 bg-white/[0.03] p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#ff6a1a]/25 hover:bg-white/[0.05]"
    >
      {hit.category_name && (
        <span className="mb-2.5 inline-block rounded-full bg-[#ff6a1a]/10 px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-[#ff6a1a]">
          {hit.category_name}
        </span>
      )}
      <h2 className="text-lg font-semibold leading-snug text-white transition-colors group-hover:text-[#ff6a1a]">
        {hit.title}
      </h2>
      {hit.snippet_html ? (
        <p
          className="mt-2 text-sm leading-relaxed text-white/56 [&_mark]:rounded-sm [&_mark]:bg-[#ff6a1a]/25 [&_mark]:px-0.5 [&_mark]:text-white"
          // Backend sanitises to <mark> only — see BlogController#search_hit.
          dangerouslySetInnerHTML={{ __html: hit.snippet_html }}
        />
      ) : (
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-white/56">{hit.description}</p>
      )}
      <div className="mt-4 flex items-center gap-2 text-xs text-white/40">
        {hit.published_at && <time>{formatDate(hit.published_at)}</time>}
        {hit.published_at && hit.reading_time && <span aria-hidden="true">·</span>}
        {hit.reading_time && <span>{hit.reading_time}</span>}
      </div>
    </Link>
  )
}

function EmptyState() {
  return (
    <div className="mt-6 rounded-2xl border border-white/8 bg-white/[0.03] px-8 py-14 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#ff6a1a]/20 bg-[#ff6a1a]/10">
        <SearchX size={22} className="text-[#ff6a1a]" aria-hidden="true" />
      </div>
      <p className="mt-4 text-base font-semibold text-white">No articles match that search</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-white/56">
        Try different keywords or fewer words — or browse everything we&rsquo;ve published so far.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link
          href="/blogs"
          className="rounded-full border border-white/10 px-5 py-2.5 text-sm font-medium text-white/70 transition-colors hover:border-white/25 hover:text-white"
        >
          Browse all articles
        </Link>
        <a href="/contact-us" className="text-sm text-[#ff6a1a] transition-colors hover:text-[#ff8c4a]">
          Ask us directly →
        </a>
      </div>
    </div>
  )
}
